/**
 * RTG Trip Overlay - Full-screen psychedelic acid/mushroom trip effect
 * Gets progressively more intense over 5 minutes
 */
import React, { useRef, useEffect, useState } from 'react';

const TripOverlay = ({ active, duration = 300, onEnd }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const startTimeRef = useRef(null);
  const [intensity, setIntensity] = useState(0);
  const [timeLeft, setTimeLeft] = useState(duration);

  useEffect(() => {
    if (!active) {
      startTimeRef.current = null;
      setIntensity(0);
      setTimeLeft(duration);
      if (animRef.current) cancelAnimationFrame(animRef.current);
      return;
    }

    startTimeRef.current = Date.now();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Kaleidoscope segments
    const SEGMENTS = 8;

    const drawKaleidoscope = (ctx, W, H, t, intensity) => {
      const cx = W / 2;
      const cy = H / 2;
      const maxR = Math.sqrt(cx * cx + cy * cy);

      ctx.save();
      ctx.translate(cx, cy);

      // Draw kaleidoscope segments
      for (let seg = 0; seg < SEGMENTS; seg++) {
        ctx.save();
        ctx.rotate((seg / SEGMENTS) * Math.PI * 2 + t * 0.1 * intensity);

        // Clip to segment
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(maxR, 0);
        ctx.arc(0, 0, maxR, 0, Math.PI * 2 / SEGMENTS);
        ctx.closePath();
        ctx.clip();

        // Draw fractal-like patterns
        const numShapes = Math.floor(4 + intensity * 8);
        for (let i = 0; i < numShapes; i++) {
          const dist = (i / numShapes) * maxR * 0.7;
          const size = 15 + intensity * 40 + Math.sin(t * 2 + i * 0.5) * 20;
          const hue = (t * 30 + i * 40 + seg * 45) % 360;
          const sat = 80 + intensity * 20;
          const light = 50 + Math.sin(t * 3 + i) * 20;
          const alpha = (0.05 + intensity * 0.15) * (0.5 + Math.sin(t * 2 + i * 0.8) * 0.5);

          ctx.fillStyle = `hsla(${hue}, ${sat}%, ${light}%, ${alpha})`;

          // Morphing shapes based on intensity
          const shapeType = Math.floor((t * 0.5 + i) % 3);
          const px = dist + Math.sin(t + i) * 20 * intensity;
          const py = Math.cos(t * 0.7 + i * 0.6) * 30 * intensity;

          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(t * 0.5 + i * 0.3);

          if (shapeType === 0) {
            // Spiraling circles
            ctx.beginPath();
            ctx.arc(0, 0, size * (0.5 + intensity * 0.5), 0, Math.PI * 2);
            ctx.fill();
          } else if (shapeType === 1) {
            // Morphing triangles
            ctx.beginPath();
            for (let p = 0; p < 3; p++) {
              const a = (p / 3) * Math.PI * 2 + t;
              const r = size * (0.4 + intensity * 0.6);
              if (p === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
              else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
            }
            ctx.closePath();
            ctx.fill();
          } else {
            // Pulsing diamonds
            const s = size * (0.3 + intensity * 0.7);
            ctx.beginPath();
            ctx.moveTo(0, -s);
            ctx.lineTo(s * 0.6, 0);
            ctx.lineTo(0, s);
            ctx.lineTo(-s * 0.6, 0);
            ctx.closePath();
            ctx.fill();
          }
          ctx.restore();
        }

        ctx.restore();
      }
      ctx.restore();
    };

    const drawSpiralVortex = (ctx, W, H, t, intensity) => {
      const cx = W / 2;
      const cy = H / 2;

      // Spiral arms
      const arms = 3 + Math.floor(intensity * 4);
      for (let arm = 0; arm < arms; arm++) {
        ctx.beginPath();
        const armOffset = (arm / arms) * Math.PI * 2;
        for (let i = 0; i < 200; i++) {
          const angle = armOffset + (i / 200) * Math.PI * 6 + t * intensity;
          const dist = (i / 200) * Math.min(W, H) * 0.5;
          const wobble = Math.sin(t * 3 + i * 0.1) * 10 * intensity;
          const px = cx + Math.cos(angle) * (dist + wobble);
          const py = cy + Math.sin(angle) * (dist + wobble);
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        const hue = (t * 40 + arm * 60) % 360;
        ctx.strokeStyle = `hsla(${hue}, 100%, 60%, ${0.1 + intensity * 0.15})`;
        ctx.lineWidth = 2 + intensity * 4;
        ctx.stroke();
      }
    };

    const drawWavyDistortion = (ctx, W, H, t, intensity) => {
      // Concentric warping rings
      const rings = Math.floor(5 + intensity * 15);
      const cx = W / 2 + Math.sin(t * 0.5) * 100 * intensity;
      const cy = H / 2 + Math.cos(t * 0.7) * 80 * intensity;

      for (let ring = 0; ring < rings; ring++) {
        const baseR = (ring / rings) * Math.min(W, H) * 0.6;
        const hue = (t * 50 + ring * 20) % 360;
        const alpha = (0.03 + intensity * 0.08) * (1 - ring / rings);

        ctx.strokeStyle = `hsla(${hue}, 100%, 65%, ${alpha})`;
        ctx.lineWidth = 3 + intensity * 3;
        ctx.beginPath();

        for (let i = 0; i <= 64; i++) {
          const angle = (i / 64) * Math.PI * 2;
          const wobble = Math.sin(angle * 6 + t * 3 + ring) * (5 + intensity * 25);
          const r = baseR + wobble;
          const px = cx + Math.cos(angle) * r;
          const py = cy + Math.sin(angle) * r;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
      }
    };

    const drawEyePatterns = (ctx, W, H, t, intensity) => {
      if (intensity < 0.4) return; // Only show at higher intensities

      const count = Math.floor(intensity * 6);
      for (let i = 0; i < count; i++) {
        const ex = W * (0.2 + Math.sin(t * 0.3 + i * 2) * 0.3);
        const ey = H * (0.2 + Math.cos(t * 0.4 + i * 1.5) * 0.3);
        const size = 20 + intensity * 40;
        const alpha = (intensity - 0.4) * 0.3;

        // Eye shape
        ctx.save();
        ctx.translate(ex, ey);
        ctx.rotate(Math.sin(t + i) * 0.3);

        // Outer eye
        const hue = (t * 60 + i * 90) % 360;
        ctx.fillStyle = `hsla(${hue}, 100%, 50%, ${alpha * 0.5})`;
        ctx.beginPath();
        ctx.ellipse(0, 0, size, size * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();

        // Iris
        ctx.fillStyle = `hsla(${(hue + 180) % 360}, 100%, 40%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(0, 0, size * 0.35, 0, Math.PI * 2);
        ctx.fill();

        // Pupil
        ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
        ctx.beginPath();
        ctx.arc(0, 0, size * 0.15, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      }
    };

    const animate = () => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      
      // Intensity curve: starts slow, accelerates, peaks near end
      // Easing: cubic ease-in for that "it keeps getting heavier" feel
      const rawIntensity = progress * progress * progress * 0.5 + progress * 0.5;
      const currentIntensity = Math.min(rawIntensity, 1);
      setIntensity(currentIntensity);
      setTimeLeft(Math.max(0, Math.floor(duration - elapsed)));

      if (elapsed >= duration) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (onEnd) onEnd();
        return;
      }

      const W = canvas.width;
      const H = canvas.height;
      const t = elapsed;

      // Clear with trailing effect (creates motion blur at higher intensities)
      const trailAlpha = 0.15 + (1 - currentIntensity) * 0.6;
      ctx.fillStyle = `rgba(0, 0, 0, ${trailAlpha})`;
      ctx.fillRect(0, 0, W, H);

      // Layer 1: Wavy distortion rings
      drawWavyDistortion(ctx, W, H, t, currentIntensity);

      // Layer 2: Kaleidoscope
      drawKaleidoscope(ctx, W, H, t, currentIntensity);

      // Layer 3: Spiral vortex (builds with intensity)
      if (currentIntensity > 0.2) {
        drawSpiralVortex(ctx, W, H, t, currentIntensity);
      }

      // Layer 4: Psychedelic eye patterns at peak
      drawEyePatterns(ctx, W, H, t, currentIntensity);

      // Layer 5: Color bleed / screen tint
      const tintHue = (t * 20) % 360;
      const tintAlpha = currentIntensity * 0.12;
      ctx.fillStyle = `hsla(${tintHue}, 100%, 50%, ${tintAlpha})`;
      ctx.fillRect(0, 0, W, H);

      // Layer 6: Scan lines at high intensity
      if (currentIntensity > 0.6) {
        const scanAlpha = (currentIntensity - 0.6) * 0.15;
        for (let y = 0; y < H; y += 3) {
          ctx.fillStyle = `rgba(0, 0, 0, ${scanAlpha})`;
          ctx.fillRect(0, y, W, 1);
        }
      }

      // Layer 7: Screen breathing / zoom pulse
      if (currentIntensity > 0.3) {
        const breathe = Math.sin(t * 0.8) * currentIntensity * 0.02;
        ctx.save();
        ctx.translate(W / 2, H / 2);
        ctx.scale(1 + breathe, 1 + breathe);
        ctx.translate(-W / 2, -H / 2);
        
        // Radial rainbow gradient that pulses
        const grad = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W * 0.6);
        const h1 = (t * 30) % 360;
        grad.addColorStop(0, `hsla(${h1}, 100%, 50%, ${currentIntensity * 0.06})`);
        grad.addColorStop(0.5, `hsla(${(h1 + 120) % 360}, 100%, 50%, ${currentIntensity * 0.04})`);
        grad.addColorStop(1, `hsla(${(h1 + 240) % 360}, 100%, 50%, ${currentIntensity * 0.03})`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [active, duration, onEnd]);

  if (!active) return null;

  // CSS filters that intensify over time
  const hueRotate = intensity * 180;
  const saturate = 1 + intensity * 2;
  const contrast = 1 + intensity * 0.3;

  return (
    <>
      {/* CSS filter overlay on the entire page */}
      <style>{`
        .nightclub-trip-active {
          filter: hue-rotate(${hueRotate}deg) saturate(${saturate}) contrast(${contrast}) ${intensity > 0.5 ? `blur(${(intensity - 0.5) * 2}px)` : ''};
          transition: filter 2s ease;
        }
      `}</style>

      {/* Canvas overlay for psychedelic effects */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 z-[55] pointer-events-none"
        style={{ mixBlendMode: 'screen' }}
        data-testid="trip-overlay-canvas"
      />

      {/* Trip intensity indicator */}
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[56] pointer-events-none"
        data-testid="trip-indicator"
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 backdrop-blur-xl border border-fuchsia-500/30">
          <div className="text-fuchsia-400 text-xs font-bold animate-pulse">RTG TRIP</div>
          <div className="w-24 h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{
                width: `${intensity * 100}%`,
                background: `linear-gradient(90deg, #22c55e, #eab308, #ef4444, #a855f7)`,
              }}
            />
          </div>
          <div className="text-white/60 text-[10px] font-mono">
            {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
          </div>
        </div>
      </div>
    </>
  );
};

export default TripOverlay;
